import type { MutationOptions } from '@shared/innmaxLib/services'
import {
  convertData,
  request,
  useMakeMutation,
} from '@shared/innmaxLib/services'
import { pathOr, prop } from 'ramda'
import React from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import {
  useConvertParkingFeeBasic,
  useConvertToParkingFee,
  useConvertToParkingFeeDiscount,
} from './parkingFee.converter'
import { IParkingFeeBasic } from './parkingFee.type'

export const useAddParkingFeeBasic = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/basic', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useUpdateParkingFeeBasic = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/basic/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useParkingFeeBasicList = (
  query?: any,
  options?: UseQueryOptions
) => {
  const converter = useConvertParkingFeeBasic()

  const { data: _data, ...others } = useQuery(['/parkingFee/basic', query], {
    ...options,
  })

  const data = React.useMemo(() => {
    return converter(pathOr([], ['data'], _data)) as IParkingFeeBasic[]
  }, [_data, converter])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}

export const useParkingFeeAreaPagination = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, ...others } = useQuery(
    ['/parkingFee/area/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const converter = useConvertToParkingFee()

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter,
      },
      data
    )
  }, [data, converter])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useAddParkingFeeArea = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/area', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useUpdateParkingFeeArea = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/area/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useDeleteParkingFeeArea = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/area/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

export const useParkingFeeArea = (query?: any, options?: UseQueryOptions) => {
  const { data: _data, ...others } = useQuery(
    ['/parkingFee/area/{id}', query],
    {
      enabled: !!query?.id,
      ...options,
    }
  )

  const converter = useConvertToParkingFee()

  const data = React.useMemo(() => {
    return converter(prop('data', _data as any))
  }, [_data, converter])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}

export const useBatchUpdateParkingFeeArea = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/area/batch', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useParkingFeeDiscountPagination = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, ...others } = useQuery(
    ['/parkingFee/discount/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const converter = useConvertToParkingFeeDiscount()

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter,
      },
      data
    )
  }, [data, converter])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useAddParkingFeeDiscount = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/discount', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useUpdateParkingFeeDiscount = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/discount/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useDeleteParkingFeeDiscount = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/parkingFee/discount/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

export const useParkingFeeDiscount = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data: _data, ...others } = useQuery(
    ['/parkingFee/discount/{id}', query],
    {
      enabled: !!query?.id,
      ...options,
    }
  )

  const converter = useConvertToParkingFeeDiscount()

  const data = React.useMemo(() => {
    return converter(prop('data', _data as any))
  }, [_data, converter])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}

export const useParkingSpaceNumbers = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data: _data, ...others } = useQuery(
    ['/parkingFee/discount/spaceNumberList', query],
    {
      ...options,
    }
  )

  const data = React.useMemo(() => {
    return pathOr([], ['data'], _data as any) as string[]
  }, [_data])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}
